import { CommonModule } from "@angular/common";

import { Component, EventEmitter, OnInit, Output } from "@angular/core";

import { finalize } from "rxjs/operators";

import {
  IntegrationsService,
  SquareSetupResponse,
} from "../../../../../shared/services/integrations.service";

import { SquareLocationsComponent } from "./locations/square-locations.component";
import { SquareServicesComponent } from "./services/square-services.component";
import { SquareStaffComponent } from "./staff/square-staff.component";

type SquareSetupSection = "locations" | "services" | "staff" | "review";

@Component({
  selector: "app-square-setup",

  standalone: true,

  imports: [
    CommonModule,
    SquareLocationsComponent,
    SquareServicesComponent,
    SquareStaffComponent,
  ],

  templateUrl: "./square-setup.component.html",

  styleUrl: "./square-setup.component.scss",
})
export class SquareSetupComponent implements OnInit {
  @Output()
  closed = new EventEmitter<void>();

  activeSection: SquareSetupSection = "locations";

  loading = true;

  setup: SquareSetupResponse | null = null;

  errorMessage = "";

  successMessage = "";

  constructor(private readonly integrationsService: IntegrationsService) {}

  ngOnInit(): void {
    this.loadSetup();
  }

  loadSetup(): void {
    this.loading = true;

    this.errorMessage = "";

    this.integrationsService
      .getSquareSetup()
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.setup = response;
        },

        error: (error) => {
          console.error("Unable to load Square setup:", error);

          this.errorMessage =
            error?.error?.detail ?? "Unable to load Square setup.";
        },
      });
  }

  selectSection(section: SquareSetupSection): void {
    this.activeSection = section;

    this.successMessage = "";

    this.errorMessage = "";
  }

  handleMappingChanged(): void {
    this.successMessage = "Square mapping updated.";

    this.loadSetup();
  }

  close(): void {
    this.closed.emit();
  }

  get locationsMapped(): number {
    return (
      this.setup?.locations.square.filter((location) => !!location.mapping)
        .length ?? 0
    );
  }

  get locationsTotal(): number {
    return this.setup?.locations.square.length ?? 0;
  }

  get servicesMapped(): number {
    return (
      this.setup?.services.square.filter((service) => !!service.mapping)
        .length ?? 0
    );
  }

  get servicesTotal(): number {
    return this.setup?.services.square.length ?? 0;
  }

  get staffMapped(): number {
    return (
      this.setup?.staff.square.filter((member) => !!member.mapping).length ?? 0
    );
  }

  get staffTotal(): number {
    return this.setup?.staff.square.length ?? 0;
  }
}
